import { Provider } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import progressionReducer from "./reducers/progressionReducer";

// Version du state - incrémenter si la structure change
const STATE_VERSION = 5;

// Charger l'état sauvegardé depuis localStorage
const loadState = () => {
  try {
    const serializedState = localStorage.getItem('saviol-state');
    if (serializedState === null) {
      return undefined;
    }
    const state = JSON.parse(serializedState);
    
    // Vérifier la version du state
    if (state && state.version !== STATE_VERSION) {
      console.log("Version du state incompatible, nettoyage du localStorage");
      localStorage.removeItem('saviol-state');
      return undefined;
    }
    
    // Vérifier si on revient de la page new-product (via sessionStorage)
    const returningFromNewProduct = sessionStorage.getItem('saviol-returning-from-new-product') === 'true';
    console.log("🔄 Chargement de l'état - Retour de new-product:", returningFromNewProduct);
    
    // Valider que l'état chargé a la bonne structure
    if (state && state.progression) {
      const prog = state.progression;
      
      // Si on ne revient PAS de new-product, c'est un rechargement normal
      // → Réinitialiser à l'état initial mais garder token/email
      if (!returningFromNewProduct) {
        console.log("🔄 Rechargement de page détecté - Réinitialisation à l'état initial");
        // Si connecté → Welcome, sinon → IntroCamera
        const initialState = prog.token ? "Welcome" : "IntroCamera";
        const initialCamera = prog.token ? "MainCamera" : "IntroCamera";
        
        return {
          progression: {
            token: prog.token || null,
            userEmail: prog.userEmail || null,
            // Tout le reste à l'état initial
            currentState: initialState,
            nextState: initialState,
            activeCamera: initialCamera,
            currentModule: "none",
            inTrans: false,
            xpStarted: prog.token ? true : false, // Si connecté, l'expérience est démarrée
            xpLoaded: prog.token ? true : false, // Si connecté, l'expérience est chargée
            showPlane: prog.token ? true : false, // Si connecté, afficher le plan
            isSubmitted: prog.token ? true : false, // Si connecté, formulaire soumis
            activeModals: [],
            savedState: null,
            skipIntroAnimation: false,
          }
        };
      }
      
      // Si on revient de new-product, charger savedState pour restauration
      console.log("✅ Retour de new-product - Chargement de savedState:", prog.savedState);
      
      // S'assurer que activeModals est toujours un tableau
      if (!Array.isArray(prog.activeModals)) {
        prog.activeModals = [];
      }
      
      // Nettoyer le flag sessionStorage après utilisation
      sessionStorage.removeItem('saviol-returning-from-new-product');
      
      // Retourner l'état avec savedState pour restauration
      return { progression: prog };
    }
    return undefined;
  } catch (err) {
    console.error("Erreur lors du chargement de l'état:", err);
    // En cas d'erreur, nettoyer le localStorage
    localStorage.removeItem('saviol-state');
    sessionStorage.removeItem('saviol-returning-from-new-product');
    return undefined;
  }
};

// Sauvegarder l'état dans localStorage
const saveState = (state) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('saviol-state', serializedState);
  } catch (err) {
    console.error("Erreur lors de la sauvegarde de l'état:", err);
  }
};

const preloadedState = loadState();

const store = configureStore({
  reducer: { progression: progressionReducer },
  preloadedState,
});

// Sauvegarder l'état à chaque modification
store.subscribe(() => {
  const state = store.getState();
  // Ne sauvegarder que les données importantes et valides
  if (state && state.progression) {
    saveState({
      version: STATE_VERSION,
      progression: {
        token: state.progression.token || null,
        userEmail: state.progression.userEmail || null,
        activeCamera: state.progression.activeCamera || "IntroCamera",
        currentState: state.progression.currentState || "IntroCamera",
        currentModule: state.progression.currentModule || "none",
        // MAINTENANT on sauvegarde savedState pour permettre la restauration après new-product
        savedState: state.progression.savedState || null,
        isSubmitted: state.progression.isSubmitted || false,
        xpStarted: state.progression.xpStarted || false,
        xpLoaded: state.progression.xpLoaded || false,
        showPlane: state.progression.showPlane || false,
        activeModals: Array.isArray(state.progression.activeModals) ? state.progression.activeModals : [],
      }
    });
  }
});

export default function StoreProvider({ children }) {
  return <Provider store={store}>{children}</Provider>;
}
